<template>
  <div class="quagga-scanner">
    <div id="scanner-container" ref="scannerContainer" class="scanner-container"></div>
    <div v-if="error" class="error-message">{{ error }}</div>
    <div v-if="debugInfo" class="debug-info">
      Last scan: {{ debugInfo }}
    </div>
    <div v-if="!hasCamera" class="alert alert-warning">
      Camera access is not supported or not allowed.
    </div>
    <div v-if="scanning" class="scan-overlay">
      <div class="scan-frame"></div>
      <div class="scan-line"></div>
    </div>
  </div>
</template>

<script>
import Quagga from 'quagga';

export default {
  name: "QuaggaScanner",
  props: {
    facingMode: {
      type: String,
      default: 'environment'
    },
    autoStart: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      scanning: false,
      error: null,
      hasCamera: false,
      lastScannedCode: null,
      scanTimeout: null,
      debugInfo: null
    };
  },
  mounted() {
    this.checkCameraSupport();
    if (this.autoStart) {
      this.startScanner();
    }
  },
  beforeDestroy() {
    this.stopScanner();
    if (this.scanTimeout) {
      clearTimeout(this.scanTimeout);
    }
  },
  methods: {
    checkCameraSupport() {
      this.hasCamera = !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
      if (!this.hasCamera) {
        this.error = 'Camera access is not supported in this browser.';
      }
    },
    
    startScanner() {
      if (!this.hasCamera) {
        return;
      }
      
      this.scanning = true;
      this.error = null;
      this.debugInfo = null;
      
      const container = this.$refs.scannerContainer;
      
      Quagga.init({
        inputStream: {
          type: "LiveStream",
          constraints: {
            facingMode: this.facingMode,
            width: { ideal: 1920 },
            height: { ideal: 1080 }
          },
          target: container,
          constraints: {
            facingMode: this.facingMode,
            aspectRatio: { ideal: 1.333 }
          }
        },
        decoder: {
          readers: [
            "code_128_reader",      // Supports full ASCII including hyphens
            "code_39_reader",       // Supports hyphens but may need config
            "code_39_vin_reader",   // VIN variant
            "code_93_reader",       // Supports hyphens
            "codabar_reader",       // Supports hyphens
            "ean_reader",
            "ean_8_reader",
            "upc_reader",
            "upc_e_reader"
          ],
          multiple: false,
          // Important: Enable debug to see what's being detected
          debug: {
            drawBoundingBox: true,
            showFrequency: true,
            drawScanline: true,
            showPattern: true
          }
        },
        locate: true,
        numOfWorkers: 2,
        frequency: 15,
        locator: {
          patchSize: "medium",
          halfSample: true
        }
      }, (err) => {
        if (err) {
          console.error('Quagga init error:', err);
          this.error = 'Failed to initialize camera. Please check permissions.';
          this.scanning = false;
          return;
        }
        
        Quagga.start();
        
        setTimeout(() => {
          this.fixVideoSizing();
        }, 100);
        
        this.attachListeners();
      });
    },
    
    fixVideoSizing() {
      const videos = document.querySelectorAll('#scanner-container video');
      const canvases = document.querySelectorAll('#scanner-container canvas');
      
      videos.forEach(video => {
        if (video) {
          video.style.position = 'absolute';
          video.style.top = '0';
          video.style.left = '0';
          video.style.width = '100%';
          video.style.height = '100%';
          video.style.objectFit = 'cover';
        }
      });
      
      canvases.forEach(canvas => {
        if (canvas) {
          canvas.style.position = 'absolute';
          canvas.style.top = '0';
          canvas.style.left = '0';
          canvas.style.width = '100%';
          canvas.style.height = '100%';
        }
      });
      
      if (Quagga.canvas && Quagga.canvas.dom) {
        const overlayCanvas = Quagga.canvas.dom.overlay;
        const imageCanvas = Quagga.canvas.dom.image;
        
        if (overlayCanvas) {
          overlayCanvas.style.position = 'absolute';
          overlayCanvas.style.top = '0';
          overlayCanvas.style.left = '0';
          overlayCanvas.style.width = '100%';
          overlayCanvas.style.height = '100%';
        }
        
        if (imageCanvas) {
          imageCanvas.style.position = 'absolute';
          imageCanvas.style.top = '0';
          imageCanvas.style.left = '0';
          imageCanvas.style.width = '100%';
          imageCanvas.style.height = '100%';
        }
      }
    },
    
    attachListeners() {
      let processingCode = false;
      
      Quagga.onDetected((result) => {
        const code = result.codeResult.code;
        const format = result.codeResult.format;
        
        // Show debug info
        this.debugInfo = `Format: ${format}, Raw: ${code}`;
        console.log('Detected:', { format, code, fullResult: result.codeResult });
        
        if (processingCode) return;
        
        if (code && this.isValidBarcode(code)) {
          processingCode = true;
          
          if (this.scanTimeout) {
            clearTimeout(this.scanTimeout);
          }
          
          this.scanTimeout = setTimeout(() => {
            processingCode = false;
          }, 1000);
          
          if (this.lastScannedCode !== code) {
            this.lastScannedCode = code;
            
            // Try to preserve hyphens if they were in the original
            // For Code 39, hyphens are valid but might be getting stripped
            let finalCode = code;
            
            // If the code looks like it should have a hyphen (e.g., letters-numbers-letters)
            if (/^[A-Z]{3}\d{3}[A-Z]{2}$/.test(code)) {
              // Check if original might have had hyphen at position 6
              if (code.length === 8) {
                finalCode = code.substring(0, 6) + '-' + code.substring(6);
              }
            }
            
            this.$emit('scan', finalCode);
          }
        }
      });
      
      Quagga.onProcessed((result) => {
        const drawingCtx = Quagga.canvas.ctx.overlay;
        const drawingCanvas = Quagga.canvas.dom.overlay;
        
        if (result && drawingCtx && drawingCanvas) {
          drawingCtx.clearRect(0, 0, parseInt(drawingCanvas.width), parseInt(drawingCanvas.height));
          
          if (result.box) {
            Quagga.ImageDebug.drawPath(result.box, { x: 0, y: 1 }, drawingCtx, { color: "#00FF00", lineWidth: 3 });
          }
          
          // Show decoded info on screen
          if (result.codeResult && result.codeResult.code) {
            drawingCtx.font = "16px Arial";
            drawingCtx.fillStyle = "#00FF00";
            drawingCtx.fillText(`${result.codeResult.format}: ${result.codeResult.code}`, 10, 30);
          }
        }
      });
      
      const observer = new MutationObserver(() => {
        this.fixVideoSizing();
      });
      
      observer.observe(this.$refs.scannerContainer, {
        childList: true,
        subtree: true
      });
      
      this.observer = observer;
    },
    
    isValidBarcode(code) {
      if (!code || code.length < 3) return false;
      
      // Updated validation to include hyphens
      const validPatterns = [
        /^\d+$/,                                    // Numbers only
        /^[A-Z0-9-]+$/,                             // Alphanumeric with hyphens
        /^[A-Za-z0-9-]+$/,                          // Alphanumeric with hyphens (case insensitive)
        /^[A-Z0-9\s-]+$/                            // Alphanumeric with spaces and hyphens
      ];
      
      return validPatterns.some(pattern => pattern.test(code));
    },
    
    stopScanner() {
      if (this.observer) {
        this.observer.disconnect();
      }
      
      if (Quagga && typeof Quagga.stop === 'function') {
        Quagga.stop();
      }
      this.scanning = false;
      
      if (this.scanTimeout) {
        clearTimeout(this.scanTimeout);
      }
    },
    
    toggleCamera() {
      const newFacingMode = this.facingMode === 'environment' ? 'user' : 'environment';
      this.$emit('update:facingMode', newFacingMode);
      this.restartScanner();
    },
    
    restartScanner() {
      this.stopScanner();
      setTimeout(() => {
        this.startScanner();
      }, 500);
    }
  },
  watch: {
    facingMode() {
      this.restartScanner();
    }
  }
};
</script>

<style scoped>
.quagga-scanner {
  position: relative;
  width: 100%;
  background: #000;
  border-radius: 12px;
  overflow: hidden;
}

.scanner-container {
  position: relative;
  width: 100%;
  padding-bottom: 75%;
  background: #000;
  overflow: hidden;
}

@media (max-width: 768px) {
  .scanner-container {
    padding-bottom: 100%;
  }
}

.scanner-container :deep(video),
.scanner-container :deep(canvas) {
  position: absolute !important;
  top: 0 !important;
  left: 0 !important;
  width: 100% !important;
  height: 100% !important;
  object-fit: cover !important;
}

.scan-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  z-index: 10;
}

.scan-frame {
  position: absolute;
  top: 20%;
  left: 10%;
  right: 10%;
  bottom: 20%;
  border: 2px solid rgba(255, 255, 255, 0.9);
  border-radius: 12px;
  box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.5);
}

.scan-line {
  position: absolute;
  top: 20%;
  left: 10%;
  right: 10%;
  height: 2px;
  background: linear-gradient(90deg, transparent, #00ff00, #00ff00, transparent);
  animation: scan 2s linear infinite;
}

@keyframes scan {
  0% {
    top: 20%;
  }
  100% {
    top: 80%;
  }
}

.error-message {
  position: absolute;
  bottom: 20px;
  left: 20px;
  right: 20px;
  text-align: center;
  font-size: 14px;
  padding: 10px;
  background: rgba(220, 53, 69, 0.9);
  color: white;
  border-radius: 6px;
  z-index: 20;
}

.debug-info {
  position: absolute;
  top: 20px;
  left: 20px;
  right: 20px;
  background: rgba(0, 0, 0, 0.7);
  color: #00ff00;
  padding: 8px;
  font-size: 12px;
  font-family: monospace;
  border-radius: 4px;
  z-index: 20;
  text-align: center;
}

.alert-warning {
  position: absolute;
  bottom: 20px;
  left: 20px;
  right: 20px;
  background-color: rgba(255, 193, 7, 0.9);
  color: #856404;
  padding: 10px;
  border-radius: 4px;
  font-size: 13px;
  text-align: center;
  z-index: 20;
}

@media (max-width: 768px) and (orientation: landscape) {
  .scanner-container {
    padding-bottom: 56.25%;
  }
  
  .scan-frame {
    top: 10% !important;
    left: 15% !important;
    right: 15% !important;
    bottom: 10% !important;
  }
  
  @keyframes scan {
    0% {
      top: 10%;
    }
    100% {
      top: 90%;
    }
  }
}
</style>


// WEBPACK FOOTER //
// src/components/importables/QuaggaScanner.vue